import { useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from '@mui/material';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import api from '../services/api';

// Shared by Survey Scripting and Survey Testing: both start by uploading a
// questionnaire to /parse-qnr/ and polling until Claude has parsed it.

export const PARSE_MESSAGES = [
  'Sending questionnaire to Claude…',
  'Reading through all your questions…',
  'Identifying question types…',
  'Mapping answer options and scales…',
  'Spotting grid questions and row statements…',
  'Detecting routing and skip logic…',
  'Extracting question IDs and aliases…',
  'Checking for disqualify and screenout markers…',
  'Running QA checks on scale labels…',
  'Looking for duplicate answer options…',
  'Verifying question completeness…',
  'Almost done, tidying things up…',
];

const QnrUploadStep = ({
  surveyId,
  onParsed,
  heading,
  subtitle,
  messages = PARSE_MESSAGES,
  estimate = 'This usually takes 30–60 seconds',
}) => {
  const inputRef = useRef();
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const pollRef = useRef(null);
  const msgRef = useRef(null);

  useEffect(() => {
    if (parsing) {
      msgRef.current = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 3500);
    } else {
      clearInterval(msgRef.current);
    }
    return () => clearInterval(msgRef.current);
  }, [parsing, messages.length]);

  useEffect(() => () => {
    clearTimeout(pollRef.current);
    clearInterval(msgRef.current);
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(docx|txt)$/i)) {
      setError('Only .docx and .txt files are supported.');
      return;
    }
    setError(null);
    setParsing(true);
    setMsgIdx(0);

    let taskId;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/surveys/${surveyId}/parse-qnr/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      taskId = data.task_id;
    } catch (err) {
      setError(err.response?.data?.error || 'Parsing failed. Please try again.');
      setParsing(false);
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/surveys/${surveyId}/parse-qnr-status/${taskId}/`);
        if (data.state === 'success') {
          setParsing(false);
          onParsed(data.questions || [], data.qa_issues || []);
        } else if (data.state === 'failure') {
          setError(data.error || 'Parsing failed. Please try again.');
          setParsing(false);
        } else {
          pollRef.current = setTimeout(poll, 2000);
        }
      } catch {
        setError('Lost connection during parsing. Please try again.');
        setParsing(false);
      }
    };

    pollRef.current = setTimeout(poll, 2000);
  };

  return (
    <Box>
      <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700} mb={1}>
        {heading}
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={3}>
        {subtitle}
      </Typography>

      <Paper
        variant="outlined"
        onClick={() => !parsing && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files[0]);
        }}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 3,
          p: 6,
          textAlign: 'center',
          cursor: parsing ? 'default' : 'pointer',
          bgcolor: dragOver ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
          '&:hover': {
            borderColor: parsing ? 'divider' : 'primary.main',
            bgcolor: parsing ? 'background.paper' : 'action.hover',
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".docx,.txt"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {parsing ? (
          <Box>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body1" fontWeight={500}>{messages[msgIdx]}</Typography>
            <Typography variant="body2" color="textSecondary" mt={0.5}>{estimate}</Typography>
          </Box>
        ) : (
          <Box>
            <UploadFileRoundedIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" fontWeight={500}>Drop your .docx or .txt file here</Typography>
            <Typography variant="body2" color="textSecondary" mt={0.5}>or click to browse</Typography>
          </Box>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
};

export default QnrUploadStep;
